import { DocumentUrlsResult } from 'shared/domain/document/documentUrlsModel';
import { documentService } from 'services/documentService';
import { documentPreviewService, fileKindService } from 'services/fileService';
import {
  getCachedResolvedPreviewState,
  cacheResolvedPreviewState,
  getSharedDocumentUrlsRequestCache,
  setSharedDocumentUrlsRequestCacheEntry,
  clearDocumentPreviewCache,
  clearDocumentUrlsRequestCache,
} from './previewUrlsCache';
import {
  DOCUMENT_ERROR_SOURCE,
  DOCUMENT_PENDING_SOURCE,
} from 'shared/domain/document/documentError';

export interface ResolvableDocument {
  _id?: string;
  localId?: number;
  thumbnail?: string;
  src?: string;
  downloadSrc?: string;
  type?: string;
}

export interface ResolvedThumbnailState {
  signedRequest: DocumentUrlsResult['documentUrls'] | undefined;
  thumbnail: string | undefined;
  src: string | undefined;
  downloadSrc: string | undefined;
}

interface ResolveOptions {
  forceRefresh?: boolean;
  retryOnMissingThumbnail?: boolean;
}

/**
 * Centralized document thumbnail resolution with caching and retry logic.
 *
 * This module consolidates the logic for:
 * 1. Checking caches (both request and resolved preview)
 * 2. Fetching signed URLs from backend
 * 3. Resolving preview sources (thumbnail, src, downloadSrc)
 * 4. Retrying when thumbnail is not yet available
 * 5. Caching results for future use
 */
export async function resolveDocumentThumbnail(
  document: ResolvableDocument,
  options: ResolveOptions = {}
): Promise<ResolvedThumbnailState | null> {
  const { forceRefresh = false, retryOnMissingThumbnail = true } = options;
  const localId = document.localId;

  if (!localId || localId === -1) {
    return null;
  }

  if (forceRefresh) {
    clearDocumentPreviewCache(document);
  }

  if (!forceRefresh) {
    const cachedPreview = getCachedResolvedPreviewState(document);
    if (cachedPreview && !isResolvedPreviewStillNeeded(cachedPreview)) {
      return {
        signedRequest: cachedPreview.signedRequest,
        thumbnail: cachedPreview.thumbnail,
        src: cachedPreview.src,
        downloadSrc: cachedPreview.downloadSrc,
      };
    }
    if (cachedPreview) {
      clearDocumentPreviewCache(document);
    }
  }

  const response = await fetchSignedUrls(localId, forceRefresh);
  if (!response) {
    return null;
  }

  if (
    retryOnMissingThumbnail &&
    needsPreviewUrlRetry(document, response.documentUrls)
  ) {
    const retryResponse = await fetchSignedUrls(localId, true);
    if (
      retryResponse &&
      !needsPreviewUrlRetry(document, retryResponse.documentUrls)
    ) {
      return resolveAndCachePreview(document, retryResponse);
    }
  }

  return resolveAndCachePreview(document, response);
}

function needsPreviewUrlRetry(
  document: ResolvableDocument,
  urls: DocumentUrlsResult['documentUrls'] | undefined
): boolean {
  if (!urls) {
    return true;
  }

  if (fileKindService.isPdf(document)) {
    return !urls.imageSignedRequest && !urls.thumbnailSignedRequest;
  }

  return !hasAnySignedPreviewUrl(urls);
}

function hasAnySignedPreviewUrl(
  urls: DocumentUrlsResult['documentUrls'] | undefined
): boolean {
  return Boolean(
    urls?.imageSignedRequest ||
      urls?.thumbnailSignedRequest ||
      urls?.signedRequest
  );
}

/**
 * Prefetch thumbnails for multiple documents without blocking.
 * Errors are silently ignored - lazy loading will retry on visibility.
 */
export function prefetchDocumentThumbnails(
  documents: ResolvableDocument[]
): void {
  documents.forEach((doc) => {
    if (hasValidCachedThumbnail(doc)) {
      return;
    }
    resolveDocumentThumbnail(doc, { retryOnMissingThumbnail: false }).catch(
      () => {
        // Ignore errors - lazy loading will retry on visibility
      }
    );
  });
}

/**
 * Check if document has a valid cached thumbnail.
 */
export function hasValidCachedThumbnail(
  document: ResolvableDocument
): boolean {
  const cached = getCachedResolvedPreviewState(document);
  return Boolean(
    cached?.thumbnail &&
      cached.thumbnail !== DOCUMENT_ERROR_SOURCE &&
      cached.thumbnail !== DOCUMENT_PENDING_SOURCE &&
      hasAnySignedPreviewUrl(cached.signedRequest)
  );
}

function isResolvedPreviewStillNeeded(cached: {
  thumbnail?: string;
  signedRequest?: DocumentUrlsResult['documentUrls'];
}): boolean {
  const thumb = cached.thumbnail;
  if (!thumb || thumb === DOCUMENT_ERROR_SOURCE || thumb === DOCUMENT_PENDING_SOURCE) {
    return true;
  }
  const urls = cached.signedRequest;
  if (!urls) {
    return true;
  }
  return !(
    urls.imageSignedRequest ||
    urls.thumbnailSignedRequest ||
    urls.signedRequest
  );
}

async function fetchSignedUrls(
  localId: number,
  forceRefresh: boolean
): Promise<DocumentUrlsResult | null> {
  const cache = getSharedDocumentUrlsRequestCache();

  if (!forceRefresh) {
    const cachedRequest = cache.get(localId);
    if (cachedRequest) {
      try {
        return await cachedRequest;
      } catch {
        return null;
      }
    }
  }

  try {
    const fetchPromise = documentService.resolveDocumentUrlsWithRetry(
      localId,
      undefined,
      0,
      forceRefresh
    );
    setSharedDocumentUrlsRequestCacheEntry(localId, fetchPromise);
    const response = await fetchPromise;
    clearDocumentUrlsRequestCache(localId);

    return response;
  } catch {
    return null;
  }
}

async function resolveAndCachePreview(
  document: ResolvableDocument,
  response: DocumentUrlsResult
): Promise<ResolvedThumbnailState> {
  const { thumbnail, src, downloadSrc } =
    await documentPreviewService.resolveDocumentPreviewSources({
      fileForDownload: document,
      existingFile: document,
      response,
    });

  const state: ResolvedThumbnailState = {
    signedRequest: response.documentUrls,
    thumbnail,
    src,
    downloadSrc,
  };

  cacheResolvedPreviewState(document, state);

  return state;
}
